import classNames from "classnames";
import { action, computed, reaction } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useEffect, useRef, useState } from "react";
import { atom } from "../atom";
import { syncCanvasIdAtom, userAtom } from "../SharedIframeState";
import { ActionType } from "../Types";
import { FeatherIcon } from "../ui/FeatherIcon";
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from "../ui/Menu";
import { Tooltip } from "../ui/Tooltip";
import { modifierKey, throttle } from "../Utils";
import { canvasDispatch } from "./CanvasDispatch";
import { canvasAtom } from "./CanvasState";
import { ParentComlinkAPI, ParentIframeEventEmitter } from "./FrameSandboxAPI";
import { applicationState, socketStateAtom } from "./Sync";

const SyncIndicator = observer(() => {
  const [shouldRenderPendingActionsAtom] = useState(() => atom(false));
  const pendingActionsTimeoutRef = useRef<number | undefined>();
  useEffect(
    () =>
      reaction(
        () => applicationState.localActions[0]?.[1][0],
        (firstLocalActionId) => {
          if (pendingActionsTimeoutRef.current !== undefined) {
            clearTimeout(pendingActionsTimeoutRef.current);
          }
          if (firstLocalActionId === undefined) {
            shouldRenderPendingActionsAtom.value = false;
          } else {
            pendingActionsTimeoutRef.current = setTimeout(
              action(() => {
                shouldRenderPendingActionsAtom.value = true;
                pendingActionsTimeoutRef.current = undefined;
              }),
              100
            );
          }
        }
      ),
    []
  );

  return (
    <Tooltip
      content={
        socketStateAtom.value === "connected"
          ? shouldRenderPendingActionsAtom.value
            ? "canvas is syncing\nto server.."
            : "canvas is synced\nto server"
          : "we're having trouble syncing"
      }
    >
      <div className="p-1 group">
        <div
          className={classNames(
            "rounded-full w-1.5 h-1.5 transition opacity-25",
            socketStateAtom.value === "connected"
              ? shouldRenderPendingActionsAtom.value
                ? "border border-green-300 bg-transparent"
                : "bg-green-200 group-hover:bg-green-300"
              : "border border-red-300 bg-transparent"
          )}
        ></div>
      </div>
    </Tooltip>
  );
});

function CanvasNameInput({
  canvasId,
  canvasName,
}: {
  canvasId: string;
  canvasName: string | undefined;
}) {
  const [canvasNameFast, setCanvasNameFast] = useState(canvasName);
  if (canvasNameFast === undefined && canvasName !== undefined) {
    setCanvasNameFast(canvasName);
  }
  const [throttleSetCanvasName] = useState(() =>
    throttle(async (name: string) => {
      canvasDispatch([ActionType.SetCanvasName, { name }]);
      ParentComlinkAPI.updateCanvasName(canvasId, name);
    }, 200)
  );
  useEffect(() => () => throttleSetCanvasName.cancel(), []);
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (
      document.activeElement !== inputRef.current &&
      canvasName !== undefined
    ) {
      setCanvasNameFast(canvasName);
      ParentComlinkAPI.updateCanvasName(canvasId, canvasName);
    }
  }, [canvasName]);

  return canvasNameFast !== undefined ? (
    <div className="h-7 relative text-xs md:text-sm">
      <input
        type="text"
        value={canvasNameFast}
        onChange={(e) => {
          setCanvasNameFast(e.target.value);
          throttleSetCanvasName(e.target.value);
        }}
        placeholder="canvas name"
        spellCheck="false"
        className="absolute top-0 left-0 w-full bottom-0 text-center bg-transparent border border-transparent hover:border-neutral-300 focus:border-neutral-400 outline-none rounded text-xs md:text-sm"
        ref={inputRef}
      />
      <div className="whitespace-pre px-2 invisible">
        {canvasNameFast !== "" ? canvasNameFast : "canvas name"}
      </div>
    </div>
  ) : null;
}

const EnvironmentVariablesAuthMenuItem = observer(
  ({ closeSettingsMenu }: { closeSettingsMenu: () => void }) => {
    const [isAuthorizedForEnvironment, setIsAuthorizedForEnvironment] =
      useState(false);
    useEffect(() => {
      async function go() {
        const isAuthorizedForEnvironment =
          await ParentComlinkAPI.isCanvasAuthorizedForEnvironment();
        setIsAuthorizedForEnvironment(isAuthorizedForEnvironment);
      }
      go();
      function onVariableChange({ variableKey }: { variableKey: string }) {
        if (variableKey === "*") {
          go();
        }
      }
      ParentIframeEventEmitter.addListener(
        "ENVIRONMENT_VARIABLE_CHANGE",
        onVariableChange
      );
      return () => {
        ParentIframeEventEmitter.removeListener(
          "ENVIRONMENT_VARIABLE_CHANGE",
          onVariableChange
        );
      };
    }, []);
    return isAuthorizedForEnvironment ? (
      <DropdownMenuItem
        onClick={async () => {
          await ParentComlinkAPI.revokeEnvironment();
          closeSettingsMenu();
        }}
      >
        revoke environment access
      </DropdownMenuItem>
    ) : null;
  }
);

export const CanvasNameUnitSandbox = observer(
  ({ canvasId }: { canvasId: string }) => {
    const canvasName = computed(() => canvasAtom.value.name).get();
    const [showSettingsMenu, setShowSettingsMenu] = useState(false);
    const isOwnCanvas = syncCanvasIdAtom.value?.[0] === "canvas";

    return (
      <>
        {canvasName !== undefined ? <SyncIndicator /> : null}
        <CanvasNameInput canvasId={canvasId} canvasName={canvasName} />
        <DropdownMenuRoot open={showSettingsMenu}>
          <DropdownMenuTrigger asChild={true}>
            <button
              onClick={() => {
                setShowSettingsMenu((v) => !v);
              }}
              className="flex text-neutral-500 p-1 -ml-1 hover:text-neutral-700 transition-colors"
            >
              <FeatherIcon icon="more-horizontal" size="sm" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuPortal>
            <DropdownMenuContent
              sideOffset={8}
              align="end"
              onEscapeKeyDown={() => {
                setShowSettingsMenu(false);
              }}
              onInteractOutside={(e) => {
                setShowSettingsMenu(false);
              }}
            >
              <DropdownMenuItem
                onClick={async () => {
                  await ParentComlinkAPI.saveCanvasToFile(
                    canvasAtom.value,
                    "header"
                  );
                  setShowSettingsMenu(false);
                }}
              >
                <div className="grow">save to file</div>
                <div className="text-neutral-400 ml-2">
                  {modifierKey === "Meta" ? "⌘" : "Ctrl-"}S
                </div>
              </DropdownMenuItem>
              {userAtom.value !== undefined ? (
                <DropdownMenuItem
                  onClick={async () => {
                    await ParentComlinkAPI.publishCanvas(canvasAtom.value);
                    setShowSettingsMenu(false);
                  }}
                >
                  {isOwnCanvas ? "clone" : "fork to"} user canvas
                </DropdownMenuItem>
              ) : null}
              {!isOwnCanvas ? (
                <EnvironmentVariablesAuthMenuItem
                  closeSettingsMenu={() => {
                    setShowSettingsMenu(false);
                  }}
                />
              ) : null}
              {isOwnCanvas ? (
                <DropdownMenuItem
                  onClick={async () => {
                    if (
                      window.confirm(
                        `Are you sure you want to delete ${canvasName ?? ""}?`
                      )
                    ) {
                      await ParentComlinkAPI.unpublishCanvas(canvasId);
                    }
                    setShowSettingsMenu(false);
                  }}
                  color="red"
                >
                  delete canvas
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenuPortal>
        </DropdownMenuRoot>
      </>
    );
  }
);
